import { useLayoutEffect, useState } from 'react';

const ANIMATION_DURATION_MS = 500;

export type AnimationPhase = 'entering' | 'exiting' | 'idle';

export function useAnimationState(shouldShow: boolean) {
	const [isRendered, setIsRendered] = useState(shouldShow);
	const [phase, setPhase] = useState<AnimationPhase>(shouldShow ? 'entering' : 'idle');
	const [prevShouldShow, setPrevShouldShow] = useState(shouldShow);

	// Handle state updates during render to ensure correct initial state for animations
	// and prevent flashing of content. This pattern triggers an immediate re-render.
	if (shouldShow !== prevShouldShow) {
		setPrevShouldShow(shouldShow);
		if (shouldShow) {
			setIsRendered(true);
			setPhase('entering');
		} else {
			setPhase('exiting');
		}
	}

	useLayoutEffect(() => {
		let rafId: number | undefined;
		let timeoutId: number | undefined;

		if (shouldShow && phase === 'entering') {
			// Double requestAnimationFrame ensures CSS transitions trigger properly.
			// rafId is reassigned twice so cleanup can cancel whichever frame is pending,
			// whether the first frame hasn't executed yet or the second is still waiting.
			rafId = requestAnimationFrame(() => {
				rafId = requestAnimationFrame(() => {
					setPhase('idle');
				});
			});
		} else if (!shouldShow && phase === 'exiting') {
			timeoutId = window.setTimeout(() => {
				setIsRendered(false);
				setPhase('idle');
			}, ANIMATION_DURATION_MS);
		}

		return () => {
			if (rafId !== undefined) cancelAnimationFrame(rafId);
			if (timeoutId !== undefined) window.clearTimeout(timeoutId);
		};
	}, [shouldShow, phase]);

	return { isRendered, phase };
}
